import { AnyObject } from "fvtt-types/utils";
import { Activity, ActivityUseConfiguration, RollDialogConfig, RollMessageConfig } from "../../globals.js";
import { debugEnabled, GameSystemConfig, i18n, warn } from "../../midi-qol.js";
import { Workflow } from "../Workflow.js";
import { replaceDefaultActivities, configSettings } from "../settings.js";
import { asyncHooksCall } from "../utils.js";
import { MidiActivityMixin, MidiActivityMixinSheet } from "./MidiActivityMixin.js";

export let MidiCastActivity: ReturnType<typeof defineMidiCastActivityClass>;
export let MidiCastSheet: ReturnType<typeof defineMidiCastSheetClass>;

export function setupCastActivity() {
  if (debugEnabled > 0) warn("MidiQOL | CastActivity | setupCastActivity | Called");
  //@ts-expect-error
  MidiCastSheet = defineMidiCastSheetClass(game.system.applications.activity.CastSheet);
  MidiCastActivity = defineMidiCastActivityClass(GameSystemConfig.activityTypes.cast.documentClass as Activity);
  if (replaceDefaultActivities) {
    // GameSystemConfig.activityTypes["dnd5eCast"] = GameSystemConfig.activityTypes.cast;
    GameSystemConfig.activityTypes.cast = { documentClass: MidiCastActivity };
  } else {
    GameSystemConfig.activityTypes["midiCast"] = { documentClass: MidiCastActivity };
  }
}
let defineMidiCastSheetClass = (baseClass: any) => {
  return class MidiCastSheet extends MidiActivityMixinSheet(baseClass) {
  }
}

let defineMidiCastActivityClass =  <T extends Activity>(ActivityClass: T) => {
  return class MidiCastActivity extends MidiActivityMixin(ActivityClass) {
    static LOCALIZATION_PREFIXES = ["midi-qol.CAST", ...super.LOCALIZATION_PREFIXES];
    static metadata =
      foundry.utils.mergeObject(
        super.metadata, {
        title: configSettings.activityNamePrefix ? "midi-qol.CAST.Title.one" : ActivityClass.metadata.title,
        dnd5eTitle: ActivityClass.metadata.title,
        sheetClass: MidiCastSheet,
        usage: {
          chatCard: "modules/midi-qol/templates/activity-card.hbs",
          dialog: ActivityClass.metadata.usage.dialog,
        },
      }, { inplace: false, insertKeys: true, insertValues: true });

    /** Resolve the effective activation type for Cast activities.
     *  1. If activation.override is set, use the Cast activity's own activation type.
     *  2. Otherwise, use the linked spell's first usable activity's activation type.
     *  3. Fall back to the parent item's activation type.
     */
    get effectiveActivationType(): string | undefined {
      // @ts-expect-error activation.override exists on dnd5e activities but not typed
      if (this.activation?.override) return this.activation.type;
      const spell = this.cachedSpell;
      if (spell) {
        const activities = spell.system?.activities;
        const activityList = (activities instanceof Collection) ? activities.contents : Object.values(activities ?? {});
        const spellActivity = activityList.find((a: any) => a.canUse) ?? activityList[0];
        if (spellActivity?.activation?.type) return spellActivity.activation.type;
      }
      // @ts-expect-error item.system.activation not typed
      return this.item?.system?.activation?.type ?? this.activation?.type;
    }

    async use(usage: ActivityUseConfiguration = {}, dialog: RollDialogConfig = {}, message: RollMessageConfig = {}) {
      if (!this.item.isEmbedded || !this.item.isOwner || !this.canUse) {
        // @ts-expect-error
        return ActivityClass.prototype.use.bind(this)(usage, dialog, message);
      }

      usage.midiOptions ??= {};
      usage.midiOptions.workflowOptions ??= {};
      usage.midiOptions.proceedChecks ??= {};

      // Create a temporary workflow so pre-checks (hooks, macros, reaction/bonus enforcement) can run
      const speaker = ChatMessage.getSpeaker({ actor: this.item.actor });
      const tempWorkflow = new Workflow(this.actor ?? null, this, speaker, null, { storeWorkflow: false });
      usage.workflow = tempWorkflow;

      try {
        // Run preTargetingChecks (incapacitated, preTargeting hooks/macros)
        if (!await this.preTargetingChecks(usage, dialog, message)) return false;

        // Run preChatChecks for the Cast activity (hooks, macros, reaction/bonus enforcement)
        // Skip spell component checks — those apply to the spell, not the Cast activity
        if (!await this.preChatChecks(usage, dialog, message, { checkComponents: false })) return false;

        // Extract reaction/bonus action state determined by preChatChecks
        const castUsesReaction = tempWorkflow.itemUsesReaction;
        const castUsesBonusAction = tempWorkflow.itemUsesBonusAction;

        // Clean up the temporary workflow before delegating to dnd5e CastActivity
        Workflow.removeWorkflow(tempWorkflow.id);
        delete usage.workflow;

        // Tell the downstream spell activity's preChatChecks to skip reaction/bonus re-checking
        usage.midiOptions.proceedChecks.checkReaction = false;
        usage.midiOptions.proceedChecks.checkBonusAction = false;

        // Pass Cast activity's enforcement results so the spell workflow can mark them as used
        usage.midiOptions.workflowOptions.castUsesReaction = castUsesReaction;
        usage.midiOptions.workflowOptions.castUsesBonusAction = castUsesBonusAction;
      } catch (err) {
        Workflow.removeWorkflow(tempWorkflow.id);
        delete usage.workflow;
        throw err;
      }
      // Process the Cast activity's own consumption (e.g., activityUses on Paladin's Smite).
      // dnd5e's CastActivity.use() bypasses Activity.use() entirely so consume() never runs.
      // The spell's config.cause mechanism also fails when the cached spell's activity has
      // consumption.spellSlot: false (ignoreLinkedConsumption becomes true).
      // Delegate to dnd5e CastActivity.use() which fetches/creates the cached spell and calls spell.use().
      // The spell's activity handles consuming the CastActivity's resources via config.cause in _prepareUsageUpdates.
      // @ts-expect-error
      return await ActivityClass.prototype.use.bind(this)(usage, dialog, message);
    }

    get possibleOtherActivity() {
      return false;
    }

    get isSelfTriggerableOnly() {
      return false;
    }

    get isTriggerableActivity() {
      return true;
    }
    
    get forcedTargetConfirmation() {
      return "never";
    }
    async _triggerSubsequentActions(config, results) {
    }
  }
}
